<template>
  <SignupTemplate>
    <FpFlexGrid compact space-around>
      <FpFlexGridItem>
        <FpHeader1>
          {{ $t('signup.common.welcome') }}
        </FpHeader1>
      </FpFlexGridItem>
      <FpFlexGridItem>
        <FpBody>{{ $t('signin.withoutPassword.subheader') }}</FpBody>
      </FpFlexGridItem>
      <FpFlexGridItem v-if="error">
        <FpAlert :data-testid="TEST_IDS.ERROR_ALERT" error>
          <FpBody>{{ $t('signup.common.genericError') }}</FpBody>
        </FpAlert>
      </FpFlexGridItem>
      <FpFlexGridItem v-if="noInviteError">
        <FpAlert :data-testid="TEST_IDS.ERROR_ALERT" error>
          <FpBody>{{ $t('signin.noInvite.error') }}</FpBody>
        </FpAlert>
      </FpFlexGridItem>
      <FpFlexGridItem>
        <FpInput
          v-model.trim="email"
          :data-testid="TEST_IDS.EMAIL_INPUT"
          :disabled="loading"
          :error="$v.email.$error"
          :label="$t('signup.common.emailLabel')"
          name="email"
          @blur="trackEmailInput"
          @keydown.enter="continueWithEmail"
        ></FpInput>
        <FpHint v-if="$v.email.$error" :data-testid="TEST_IDS.EMAIL_REQUIRED_HINT" error>
          {{ $t('signup.common.validation.email') }}
        </FpHint>
      </FpFlexGridItem>
      <FpFlexGridItem>
        <FpButton
          :data-testid="TEST_IDS.LOGIN_BUTTON"
          :loading="loading"
          fluid
          @click="continueWithEmail"
        >
          {{ $t('common.button.continue') }}
        </FpButton>
      </FpFlexGridItem>
    </FpFlexGrid>
  </SignupTemplate>
</template>

<script>
import {
  FlexAlignItems,
  FlexDirection,
  FpAlert,
  FpBody,
  FpButton,
  FpFlexGrid,
  FpFlexGridItem,
  FpHeader1,
  FpInput,
  MediaSection
} from '@flatfair/vue-fairplay';
import { FpHint } from '@flatfair/vue-fairplay/atoms';
import SignupTemplate from '@/components/page_templates/signup';
import { InviteAccountsApi, UserLoginApi } from '@/libs/apis';
import houseAndBuilding from '@/images/house_and_building.png';
import { ROUTES, USER_TYPE } from '@/pages/login/index';
import { ROUTES as SIGNUP_REDIRECT_ROUTES } from '@/pages/signup';
import { validationMixin } from 'vuelidate';
import { trackGaMixin } from '@/libs/mixins/trackGaMixin';
import { email, required } from 'vuelidate/lib/validators';
import { EMAIL_ACCOUNT_STATUS } from '@/pages/login/constants';

export const TEST_IDS = {
  EMAIL_INPUT: 'login__email_input',
  LOGIN_BUTTON: 'login__login_button',
  ERROR_ALERT: 'login__error_alert',
  EMAIL_REQUIRED_HINT: 'login__email_required_hint'
};

export default {
  components: {
    FpFlexGrid,
    FpFlexGridItem,
    FpHeader1,
    FpBody,
    FpButton,
    FpInput,
    FpAlert,
    FpHint,
    SignupTemplate
  },
  mixins: [validationMixin, trackGaMixin],
  validations: {
    email: { required, email }
  },
  data: function() {
    return {
      loading: false,
      error: false,
      email: '',
      noInviteError: false
    };
  },
  created() {
    this.houseAndBuilding = houseAndBuilding;
    this.FlexDirection = FlexDirection;
    this.FlexAlignItems = FlexAlignItems;
    this.MediaSection = MediaSection;
    this.TEST_IDS = TEST_IDS;
    this.api = new InviteAccountsApi();
    this.user_api = new UserLoginApi();
    this.trackEvent.init('Sign in | With password');
  },
  methods: {
    trackEmailInput() {
      this.trackEvent.input({ label: 'Enter email address' });
    },
    redirectSignup(userType) {
      if (userType === USER_TYPE.AGENT) {
        this.$router.push({
          name: SIGNUP_REDIRECT_ROUTES.AGENT_SIGNUP,
          params: { email: this.email }
        });
      } else if (userType === USER_TYPE.LANDLORD) {
        this.$router.push({
          name: SIGNUP_REDIRECT_ROUTES.LANDLORD_SIGNUP,
          params: { email: this.email }
        });
      } else if (userType === USER_TYPE.TENANT) {
        this.$router.push({
          name: SIGNUP_REDIRECT_ROUTES.TENANT_SIGNUP,
          params: { email: this.email }
        });
      }
    },
    goToNextRoute(accountStatus) {
      const route = this.getRoute(accountStatus);

      this.$router.push({
        name: route,
        params: { email: this.email, next: this.$route.query.next }
      });
    },
    getRoute(accountStatus) {
      switch (accountStatus) {
        case EMAIL_ACCOUNT_STATUS.NO_ACCOUNT_NO_EMAIL:
          // Other user types handled in redirect, this is only for guarantors.
          return ROUTES.CHECK_YOUR_MAILBOX;
        case EMAIL_ACCOUNT_STATUS.ACCOUNT_EXISTS_NO_PASSWORD:
          return ROUTES.CHECK_YOUR_MAILBOX;
        case EMAIL_ACCOUNT_STATUS.ACCOUNT_EXISTS_PASSWORD_EXISTS:
          return ROUTES.SIGN_IN_WITH_PASSWORD;
      }
    },
    async continueWithEmail() {
      this.trackEvent.button({ label: 'Click sign in' });
      this.$v.$touch();
      if (this.$v.$invalid) return;
      this.error = false;
      this.noInviteError = false;
      this.loading = true;
      try {
        const response = await this.user_api.registrationStatus(this.email);
        const accountStatus = response.data.account_status;
        const userType = response.data.user_type;

        if (accountStatus === EMAIL_ACCOUNT_STATUS.NO_ACCOUNT_NO_EMAIL && userType === null) {
          this.noInviteError = true;
          return;
        }
        if (userType && userType !== USER_TYPE.GUARANTOR) {
          return this.redirectSignup(userType);
        }
        await this.user_api.updateInvitesForUser(this.email);
        this.goToNextRoute(accountStatus);
      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
